import { useState } from 'react';
import {
  Button,
  Checkbox,
  DatePicker,
  MessagePlugin,
  Textarea,
  Upload,
  type UploadFile,
  type UploadProps,
} from 'tdesign-react';
import { CommandType, SUB_CATEGORY_OPTIONS } from '@/services/command';
import { createMyCommand, uploadMyCommandFiles } from '@/services/portal-command';
import { usePortalAccount } from '@/contexts/PortalAccountContext';

const ALLOWED_EXTS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.zip',
  '.rar',
  '.csv',
  '.txt',
  '.png',
  '.jpg',
  '.jpeg',
];
/** 单文件大小上限（与后端 MAX_FILE_SIZE 保持一致） */
const MAX_FILE_SIZE = 50 * 1024 * 1024;
/** 一次最多上传的文件数（与后端 MAX_FILE_COUNT 保持一致） */
const MAX_FILE_COUNT = 10;
/** 全部文件总大小上限（与后端 MAX_TOTAL_FILE_SIZE 保持一致） */
const MAX_TOTAL_FILE_SIZE = 100 * 1024 * 1024;

/**
 * 当前仅开放「个保/网安线索」一种指令类型，前台提交时前端不再让用户选择类型，
 * 而是直接固定使用该类型，后端字段仍保留以便后续扩展。
 */
const DEFAULT_COMMAND_TYPE: CommandType = CommandType.NotifyOnly;

/**
 * 支持二级分类多选的单位关键字。
 *
 * 目前只放开「上海管局」——当账号 organization 命中该关键字时，
 * 允许一次勾选多个二级分类，前端会按每个分类各调用一次 create 接口，
 * 生成多条指令（文件仅上传一次，多条指令共用同一份 fileKey）。
 *
 * 其他单位仍走"单选"逻辑。
 */
const MULTI_SUB_CATEGORY_ORG_KEYWORDS = ['上海管局'];

/** 判断当前登录人所属单位是否支持二级分类多选 */
const isMultiSubCategoryOrg = (organization: string | null | undefined): boolean => {
  const org = (organization || '').trim();
  if (!org) return false;
  return MULTI_SUB_CATEGORY_ORG_KEYWORDS.some((k) => org.includes(k));
};

const UPLOAD_LOCALE = {
  triggerUploadText: {
    fileInput: '选择文件',
    normal: '选择文件',
    reupload: '继续添加',
    delete: '删除',
  },
  dragger: {
    clickAndDragText: '拖拽文件到此处，或点击选择文件',
    draggingText: '释放鼠标以上传文件',
    dragDropText: '释放鼠标以上传文件',
  },
};

interface SubmitTabProps {
  /** 提交成功回调（由父级跳转到历史记录 Tab） */
  onSubmitted: () => void;
}

/** 单个文件类型/大小校验，通过返回 null，否则返回错误信息 */
const validateSingleFile = (raw: File): string | null => {
  const name = raw.name || '';
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')).toLowerCase() : '';
  if (!ALLOWED_EXTS.includes(ext)) {
    return `不支持的文件类型（允许：${ALLOWED_EXTS.join(', ')}）`;
  }
  if (raw.size > MAX_FILE_SIZE) {
    return `文件「${name}」超过单文件 50MB 上限`;
  }
  return null;
};

/** 指令内容前端最大长度（与后端 DTO @MaxLength(5000) 一致） */
const MAX_CONTENT_LENGTH = 5000;

const SubmitTab = ({ onSubmitted }: SubmitTabProps) => {
  // 是否允许二级分类多选（依据当前账号 organization 判定，仅"上海管局"开放）
  //
  // account 从内存 Context 读，用户无法通过改 localStorage 把 organization
  // 伪造成"上海管局"来骗过多选开关；同时，后端 create 接口在收到多次调用时
  // 仍然按 JWT 反查真实 organization 做业务处理，前端只是 UI 层的便捷开关。
  const { account } = usePortalAccount();
  const allowMultiSubCategory = isMultiSubCategoryOrg(account?.organization);
  /**
   * 已选二级分类列表。
   *  - 单选场景：数组长度 0 或 1；
   *  - 多选场景（上海管局）：可选多个，提交时会按每个分类各调一次 create 生成多条指令。
   */
  const [subCategories, setSubCategories] = useState<number[]>([]);
  const [needReply, setNeedReply] = useState(false);
  /**
   * 截止日期（精确到日，`YYYY-MM-DD` 字符串）；仅 needReply=true 时可编辑。
   *  - 未选择：空串
   *  - 后端允许 null，即勾选了需回复但不填也可以提交（业务放宽）
   *  - 截止日 11:00 由外部定时器扫描"未完成"发主群提醒
   */
  const [deadlineDate, setDeadlineDate] = useState('');

  /** 二级分类点击：多选场景切换 add/remove；单选场景直接覆盖为当前值 */
  const toggleSubCategory = (value: number) => {
    if (allowMultiSubCategory) {
      setSubCategories((prev) =>
        prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
      );
      return;
    }
    setSubCategories((prev) => (prev[0] === value ? [] : [value]));
  };
  /** 指令内容（可选，与文件二选一，都填也允许） */
  const [content, setContent] = useState('');
  /**
   * 待提交的文件列表 —— 只在前端内存里暂存，用户点「提交指令」时才一次性 multipart 上传，
   * 由服务端在同一请求内完成"单文件直传 or 多文件打包 zip 再传 COS"。
   *
   * 好处：
   *   - 避免用户"选完文件却没提交"就把孤儿文件传到 COS；
   *   - 避免多文件时反复重传/打包造成浪费；
   *   - 与后端 FilesInterceptor('files', 10) 一次性对齐，无中间态。
   */
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [submitting, setSubmitting] = useState(false);

  /**
   * 文件列表变化：加入新文件时做数量/单文件/总大小校验。
   *
   * TDesign Upload 在 `autoUpload={false} + multiple` 模式下每次 onChange 传的是
   * "累加后的完整列表"（包含旧文件 + 本次新增），因此我们只需要基于最终 list 做整体校验。
   */
  const handleFileChange: UploadProps['onChange'] = (files) => {
    const next = files ?? [];

    // 校验数量
    if (next.length > MAX_FILE_COUNT) {
      MessagePlugin.error(`最多上传 ${MAX_FILE_COUNT} 个文件`);
      return;
    }

    // 逐个校验单文件类型/大小 + 累加总大小
    let totalSize = 0;
    for (const item of next) {
      const raw = item.raw as File | undefined;
      // 兼容"从外层塞进来的、没有 raw"的兜底记录，直接跳过大小/类型校验
      if (!raw) continue;
      const err = validateSingleFile(raw);
      if (err) {
        MessagePlugin.error(err);
        return;
      }
      totalSize += raw.size;
    }
    if (totalSize > MAX_TOTAL_FILE_SIZE) {
      MessagePlugin.error(`所有文件总大小不能超过 ${MAX_TOTAL_FILE_SIZE / 1024 / 1024}MB`);
      return;
    }

    // 全部通过 → 更新列表（每项状态设为 waiting，提示"等待提交时上传"）
    setFileList(
      next.map((item) => ({
        ...item,
        status: 'waiting',
        percent: 0,
      })),
    );
  };

  const handleSubmit = async () => {
    if (subCategories.length === 0) {
      MessagePlugin.warning('请选择指令分类');
      return;
    }

    const trimmedContent = content.trim();
    const hasContent = trimmedContent.length > 0;
    const hasFile = fileList.length > 0;
    // 业务规则：内容 / 文件 至少存在其一
    if (!hasContent && !hasFile) {
      MessagePlugin.error('请填写指令内容或上传文件（至少一项）');
      return;
    }
    if (trimmedContent.length > MAX_CONTENT_LENGTH) {
      MessagePlugin.error(`指令内容长度不能超过 ${MAX_CONTENT_LENGTH} 字`);
      return;
    }

    // 收集 raw File 引用（Upload 里没 raw 的项跳过 —— 正常路径下不会出现）
    const rawFiles = hasFile
      ? fileList.map((f) => f.raw as File | undefined).filter((f): f is File => !!f)
      : [];
    if (hasFile && rawFiles.length === 0) {
      MessagePlugin.error('未识别到有效文件，请重新选择');
      return;
    }

    setSubmitting(true);
    try {
      // 1) 若有文件：一次性 multipart 上传，服务端在同一请求内完成打包 + 传 COS；
      //    若无文件：跳过上传，直接落库（仅文本内容）
      let meta: { fileKey: string; fileName: string; fileSize: number } | null = null;
      if (rawFiles.length > 0) {
        setFileList((prev) => prev.map((f) => ({ ...f, status: 'progress', percent: 0 })));
        meta = await uploadMyCommandFiles(rawFiles, (percent) => {
          setFileList((prev) => prev.map((f) => ({ ...f, status: 'progress', percent })));
        });
        setFileList((prev) => prev.map((f) => ({ ...f, status: 'success', percent: 100 })));
      }

      // 2) 再落库：commandType 前端固定为「个保/网安线索」；content / 文件三元组按需带上。
      //    若一次选中多个二级分类（仅上海管局可用），按顺序对每个分类各调一次 create，
      //    生成多条指令，多条指令共用同一份已上传的 fileKey。
      //    使用串行 for-await 是为了在其中一条失败时能立即中断、并把错误抛出；
      //    避免使用 Promise.all 时"部分成功部分失败"导致的用户认知混乱。
      const basePayload = {
        commandType: DEFAULT_COMMAND_TYPE,
        needReply,
        // 仅在勾选了「需回复」且用户选择了日期时才带 deadlineDate；否则省略。
        // 后端 DTO 允许省略；即使前端没勾却误填，后端 create() 里也会因 needReply=false 丢弃。
        ...(needReply && deadlineDate ? { deadlineDate } : {}),
        ...(hasContent ? { content: trimmedContent } : {}),
        ...(meta ?? {}),
      };
      for (const sc of subCategories) {
        await createMyCommand({ ...basePayload, subCategory: sc });
      }
      MessagePlugin.success(
        subCategories.length > 1 ? `提交成功，已生成 ${subCategories.length} 条指令` : '提交成功',
      );
      setSubCategories([]);
      setNeedReply(false);
      setDeadlineDate('');
      setContent('');
      setFileList([]);
      onSubmitted();
    } catch (err) {
      // 上传/创建失败：把列表状态置回 waiting，让用户可再次点提交
      setFileList((prev) => prev.map((f) => ({ ...f, status: 'waiting', percent: 0 })));
      const msg = err instanceof Error ? err.message : '提交失败';
      MessagePlugin.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const hasAnyPayload = content.trim().length > 0 || fileList.length > 0;
  const canSubmit = subCategories.length > 0 && hasAnyPayload && !submitting;

  return (
    <div className="portal-card">
      {/* —— 选择二级分类 —— */}
      <div className="portal-section-title">
        选择分类
        {allowMultiSubCategory ? (
          <span className="portal-section-hint">（可多选，将按每个分类各生成一条指令）</span>
        ) : null}
      </div>
      <div className="sub-category-grid">
        {SUB_CATEGORY_OPTIONS.map((opt) => {
          const isActive = subCategories.includes(opt.value);
          return (
            <button
              type="button"
              key={opt.value}
              className={`sub-category-card${isActive ? ' is-active' : ''}`}
              onClick={() => toggleSubCategory(opt.value)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* —— 指令内容 —— */}
      <div className="portal-section-title with-top">
        指令内容
        <span className="portal-section-hint">（与文件至少填写一项）</span>
      </div>
      <div className="content-wrap">
        {/*
          TDesign Textarea 在设置 `maxlength` 后会自动渲染右下角字符计数器，
          因此这里不再手写 counter，避免和内置计数重复展示（此前出现过 2 个 6/5000）。
        */}
        <Textarea
          value={content}
          onChange={(v) => setContent(String(v ?? ''))}
          placeholder="可在此填写指令的文字说明，例如线索来源 / 处置要求等；如仅提交文件可留空。"
          autosize={{ minRows: 4, maxRows: 10 }}
          maxlength={MAX_CONTENT_LENGTH}
          disabled={submitting}
        />
      </div>

      {/* —— 上传文件 —— */}
      <div className="portal-section-title with-top">
        上传文件
        <span className="portal-section-hint">（与内容至少填写一项）</span>
      </div>
      <div className="upload-wrap">
        <Upload
          theme="file-flow"
          draggable
          multiple
          autoUpload={false}
          max={MAX_FILE_COUNT}
          accept={ALLOWED_EXTS.join(',')}
          files={fileList}
          tips={`最多 ${MAX_FILE_COUNT} 个文件，单文件 ≤ 50MB，合计 ≤ 100MB；多文件将自动打包为 zip。支持 Word / PDF / Excel / 图片 / ZIP / RAR 等。`}
          locale={UPLOAD_LOCALE}
          onChange={handleFileChange}
          disabled={submitting}
          // file-flow 主题默认会在文件列表下方渲染「选择文件」「取消上传」两个按钮：
          //   - 「选择文件」与上方的拖拽区域功能重复
          //   - 「取消上传」用于触发/取消组件内置上传，而我们的场景走"提交时统一上传"，
          //     这个按钮点了没有任何效果
          // 因此直接置 null 隐藏，避免误导用户。
          uploadButton={null}
          cancelUploadButton={null}
        />
      </div>

      {/* —— 是否需要回复 —— */}
      <div className="portal-section-title with-top">反馈要求</div>
      <div className="need-reply-row">
        <Checkbox
          checked={needReply}
          onChange={(v) => {
            const next = Boolean(v);
            setNeedReply(next);
            // 取消勾选时清空截止日期，避免"看似不需回复但仍带截止日"的脏数据
            if (!next) setDeadlineDate('');
          }}
        >
          若需反馈通知/处置结果，请勾选。
        </Checkbox>
        {/* <div className="need-reply-tip">勾选后，必须上传回执附件才可完成。</div> */}
      </div>

      {/*
        截止日期选择器：仅"需回复"勾选后出现。
        - 允许留空（业务放宽：勾选了需回复但没定死时间也可以提交）
        - disableDate 屏蔽今天之前的日期，避免用户选到过去
        - TDesign DatePicker 在 valueType='YYYY-MM-DD' 时 onChange 直接给字符串
      */}
      {needReply ? (
        <div className="deadline-row">
          <span className="deadline-label">反馈日期：</span>
          <DatePicker
            value={deadlineDate}
            valueType="YYYY-MM-DD"
            format="YYYY-MM-DD"
            placeholder="（可留空）"
            clearable
            disableDate={{ before: new Date().toISOString().slice(0, 10) }}
            onChange={(v) => setDeadlineDate(typeof v === 'string' ? v : '')}
            disabled={submitting}
          />
        </div>
      ) : null}

      {/* —— 提交按钮 —— */}
      <div className="portal-submit-row">
        <Button
          theme="primary"
          size="large"
          loading={submitting}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          提交指令
        </Button>
      </div>
    </div>
  );
};

export default SubmitTab;
